/* eslint-disable @typescript-eslint/no-explicit-any */
import { compose, eq, get, keys as lodashKeys, map } from 'lodash/fp';
import { StringMap, TFunction, TFunctionKeys, TFunctionResult, TOptions } from 'i18next';
import { Theme } from '@material-ui/core';
import { Identified, Named } from '../../types';
import { Options } from '../../components/forms/fields/types';
import { Color } from '../../server-types';
import { VenueType } from '../../generated/graphql';

type Translations = Record<VenueType, TArgs>;
interface GenerateTranslationByVenueType {
    translations: Translations;
    venueType: VenueType;
}

export const generateTranslationByVenueType = ({ venueType, translations }: GenerateTranslationByVenueType) => {
    if (venueType) {
        return translations[venueType];
    }

    return translations[VenueType.Restaurant];
};

type IdMapper = (ar: any) => string[];

export const mapIds: IdMapper = map(get('id'));

export const mapOptions = map<Identified & Named, Options>(({ id, name }) => ({ id, name }));

export const getId = get('id');

interface GetFullNameArgs {
    firstName: string;
    lastName: string;
}

export const getFullName = ({ firstName, lastName }: GetFullNameArgs) => `${firstName} ${lastName}`;
export const getInitials = ({ firstName, lastName }: GetFullNameArgs) => `${firstName[0]}${lastName[0]}`;

export const mapLocalizedEnumOptions = (t: TFunction) => (objectEnum: Record<string, Record<string, string>>) => {
    const enumName = Object.keys(objectEnum)[0];
    const passedEnum = objectEnum[enumName];
    const enumKeys = Object.keys(passedEnum);
    return enumKeys.map(key => ({ id: key, name: t(key, { ns: 'enums', context: enumName }) }));
};

export const localizeEnum = (t: TFunction) => (enumValue: string, objectEnum: Record<string, Record<string, string>>) => {
    const enumName = Object.keys(objectEnum)[0];

    return t(enumValue, { ns: 'enums', context: enumName });
};

export const mapEnumOptions = (t: TFunction) => compose(
    map<string, Options>(role => ({ id: role, name: t(role) })),
    lodashKeys,
);

export function translateAndCastToString<
    TKeys
>(
    keys: TKeys
) {
    return translate(keys) as unknown as string;
}

export function translate<
// eslint-disable-next-line @typescript-eslint/ban-types
    TKeys, TInterpolationMap extends object = StringMap
>(
    keys: TKeys | TKeys[],
    options?: TOptions<TInterpolationMap> | string
): [TKeys | TKeys[], TOptions<TInterpolationMap> | string];

export function translate<
    TResult extends TFunctionResult = string,
    TKeys extends TFunctionKeys = string,
    // eslint-disable-next-line @typescript-eslint/ban-types
    TInterpolationMap extends object = StringMap
>(
    key: TKeys | TKeys[],
    defaultValue?: string,
    options?: TOptions<TInterpolationMap> | string,
): [TKeys | TKeys[], TOptions<TInterpolationMap> | string];
export function translate(...args: TFunctionResult[]) {
    return args;
}

export type TArgs = ReturnType<typeof translate>;

type FormValues<T extends Record<string, any>> = T;

export const sanitizeFormValues = <T extends Record<string, any>>(formValues: FormValues<T>, initialState?: FormValues<T>) => {
    const keys = Object.keys(formValues);

    const finalValues = keys.reduce((acc, key) => {
        const isEmpty = formValues[key] === '';

        const isPristine = initialState ? eq(formValues[key], initialState[key]) : false;
        if (!isEmpty && !isPristine) {
            return { ...acc, [key]: formValues[key] };
        }
        return acc;
    }, {} as FormValues<T>);

    return finalValues;
};

export const enumToPrimaryColorMap = (theme: Theme, color: Color) => {
    switch (color) {
        case Color.Blue: return theme.palette.additional.blue;
        case Color.Green: return theme.palette.additional.green;
        case Color.Orange: return theme.palette.additional.orange;
        case Color.Purple: return theme.palette.additional.purple;
        default:
            return '#fff';
    }
};

export const enumToHighlightColorMap: Record<Color, string> = {
    [Color.Blue]: '#D6F5FF',
    [Color.Green]: '#DCF7E5',
    [Color.Orange]: '#FFEFE2',
    [Color.Purple]: '#F6E1F9',
};

export * from './childCategoryOptions';
export * from './checkAccess';
